/**
 * Documentos por máquina — PROTÓTIPO, dados de demonstração.
 *
 * O acervo documental da máquina (projeto elétrico, laudo, manual, plano de
 * adequação...) ainda não existe no banco: hoje o backend só guarda documento
 * preso à apreciação. Esta camada existe para a tela poder ser desenhada e
 * apresentada antes disso — ela inventa um acervo ESTÁVEL por máquina e guarda
 * no `localStorage` o que a pessoa adiciona durante a demonstração.
 *
 * Estável quer dizer: a mesma máquina mostra sempre os mesmos documentos, em
 * qualquer navegador e a qualquer momento, porque tudo sai de um gerador
 * determinístico semeado pelo id da máquina — nada de `Math.random()`, que
 * faria a lista mudar a cada render e denunciar a demonstração.
 *
 * Quando o acervo virar tabela de verdade, o que sai daqui é substituído por
 * chamadas ao `apiService`: a forma de `DocumentoMaquina` é a mesma que a API
 * deveria devolver.
 */

export type StatusDocumentoMaquina = 'pendente' | 'em_analise' | 'entregue';

/** Ids dos tipos; o rótulo mora em `documentos.tipo.<id>` no dicionário. */
export const TIPOS_DOCUMENTO_MAQUINA = [
  'apreciacao_risco',
  'projeto_eletrico',
  'diagrama_pneumatico',
  'manual_operacao',
  'laudo_nr12',
  'certificado_componentes',
  'plano_adequacao',
  'relatorio_fotografico',
  'ficha_tecnica',
  'checklist_seguranca',
] as const;

export type TipoDocumentoMaquina = (typeof TIPOS_DOCUMENTO_MAQUINA)[number];

export const STATUS_DOCUMENTO_MAQUINA: StatusDocumentoMaquina[] = [
  'pendente',
  'em_analise',
  'entregue',
];

/** Sigla que compõe o código do documento (`PE-0142`). Não é texto de tela. */
const SIGLA_TIPO: Record<TipoDocumentoMaquina, string> = {
  apreciacao_risco: 'AR',
  projeto_eletrico: 'PE',
  diagrama_pneumatico: 'DP',
  manual_operacao: 'MO',
  laudo_nr12: 'LN',
  certificado_componentes: 'CC',
  plano_adequacao: 'PA',
  relatorio_fotografico: 'RF',
  ficha_tecnica: 'FT',
  checklist_seguranca: 'CS',
};

export interface DocumentoMaquina {
  id: string;
  maquinaId: string;
  tipo: string;
  codigo: string;
  revisao: number;
  status: StatusDocumentoMaquina;
  responsavel: string;
  /** Emissão; `null` enquanto o documento não existe (pendente). */
  emitidoEm: string | null;
  /** Prazo combinado — é o que define o atraso. */
  prazo: string;
  arquivo?: string;
  observacao?: string;
  /** `mock` veio do gerador; `local` foi adicionado na demonstração. */
  origem: 'mock' | 'local';
}

const RESPONSAVEIS = [
  'D. Júnior',
  'M. Alves',
  'R. Tavares',
  'A. Beatriz',
  'L. Fontana',
  'C. Moretti',
];

/**
 * Âncora das datas. Fixa de propósito: se as datas saíssem de `Date.now()`, a
 * mesma máquina mudaria de prazo entre uma abertura e outra.
 */
const EPOCA_MOCK = Date.UTC(2026, 7, 15);
const UM_DIA = 24 * 60 * 60 * 1000;

const CHAVE_ARMAZENAMENTO = 'riskguard.mock.documentosMaquina.v1';
/** Avisa as telas abertas que o acervo mudou (a lista e o dashboard escutam). */
export const EVENTO_DOCUMENTOS_MAQUINA = 'riskguard:documentos-maquina';

interface OverlayMaquina {
  adicionados: DocumentoMaquina[];
  removidos: string[];
  status: Record<string, StatusDocumentoMaquina>;
}

type Overlay = Record<string, OverlayMaquina>;

// ---------------------------------------------------------------- determinismo

/** FNV-1a: hash barato e estável entre navegadores. */
function semear(texto: string): number {
  let h = 2166136261;
  for (let i = 0; i < texto.length; i += 1) {
    h ^= texto.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Gerador congruente linear: mesma semente, mesma sequência. */
function criarSorteio(semente: number) {
  let estado = semente || 1;
  return (teto: number) => {
    estado = (Math.imul(estado, 1664525) + 1013904223) >>> 0;
    return estado % teto;
  };
}

function dataIso(diasDesdeEpoca: number): string {
  return new Date(EPOCA_MOCK + diasDesdeEpoca * UM_DIA).toISOString();
}

// ------------------------------------------------------------------- overlay

function lerOverlay(): Overlay {
  if (typeof localStorage === 'undefined') return {};
  try {
    const bruto = localStorage.getItem(CHAVE_ARMAZENAMENTO);
    const parseado = bruto ? JSON.parse(bruto) : {};
    return parseado && typeof parseado === 'object' ? (parseado as Overlay) : {};
  } catch {
    return {};
  }
}

function gravarOverlay(overlay: Overlay): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(CHAVE_ARMAZENAMENTO, JSON.stringify(overlay));
  } catch (erro) {
    console.warn('[mock-documentos] não foi possível gravar o acervo local:', erro);
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(EVENTO_DOCUMENTOS_MAQUINA));
  }
}

function overlayDaMaquina(overlay: Overlay, maquinaId: string): OverlayMaquina {
  const atual = overlay[maquinaId];
  return {
    adicionados: Array.isArray(atual?.adicionados) ? atual!.adicionados : [],
    removidos: Array.isArray(atual?.removidos) ? atual!.removidos : [],
    status: atual?.status && typeof atual.status === 'object' ? atual.status : {},
  };
}

// -------------------------------------------------------------------- geração

/**
 * Acervo inventado da máquina. De 3 a 6 documentos, sem tipo repetido, com uma
 * mistura de entregue / em análise / pendente que deixa o dashboard com algo
 * para filtrar.
 */
function gerarDocumentos(maquinaId: string): DocumentoMaquina[] {
  const sorteio = criarSorteio(semear(maquinaId));
  const quantidade = 3 + sorteio(4);

  const disponiveis = [...TIPOS_DOCUMENTO_MAQUINA];
  const documentos: DocumentoMaquina[] = [];

  for (let i = 0; i < quantidade && disponiveis.length > 0; i += 1) {
    const tipo = disponiveis.splice(sorteio(disponiveis.length), 1)[0];

    // 0-3 entregue, 4-5 em análise, 6-9 pendente: sobra pendência em quase toda
    // máquina, que é o que a tela precisa mostrar.
    const dado = sorteio(10);
    const status: StatusDocumentoMaquina =
      dado <= 3 ? 'entregue' : dado <= 5 ? 'em_analise' : 'pendente';

    const prazoEmDias = sorteio(45) - 22;
    const revisao = status === 'entregue' ? sorteio(3) : 0;

    documentos.push({
      id: `${maquinaId}:${tipo}`,
      maquinaId,
      tipo,
      codigo: `${SIGLA_TIPO[tipo]}-${String(sorteio(9000) + 1000)}`,
      revisao,
      status,
      responsavel: RESPONSAVEIS[sorteio(RESPONSAVEIS.length)],
      emitidoEm: status === 'pendente' ? null : dataIso(prazoEmDias - 5 - sorteio(10)),
      prazo: dataIso(prazoEmDias),
      origem: 'mock',
    });
  }

  return documentos;
}

// ---------------------------------------------------------------------- leitura

/** Acervo da máquina: o gerado, sem o que foi removido, mais o que foi adicionado. */
export function listarDocumentosMaquina(maquinaId: string): DocumentoMaquina[] {
  if (!maquinaId) return [];
  const { adicionados, removidos, status } = overlayDaMaquina(lerOverlay(), maquinaId);
  const removidosSet = new Set(removidos);

  return [...gerarDocumentos(maquinaId), ...adicionados]
    .filter((doc) => !removidosSet.has(doc.id))
    .map((doc) => (status[doc.id] ? { ...doc, status: status[doc.id] } : doc))
    .sort((a, b) => {
      // Pendência primeiro; dentro de cada grupo, prazo mais apertado no topo.
      const peso = (d: DocumentoMaquina) =>
        d.status === 'pendente' ? 0 : d.status === 'em_analise' ? 1 : 2;
      const diferenca = peso(a) - peso(b);
      return diferenca !== 0 ? diferenca : a.prazo.localeCompare(b.prazo);
    });
}

export interface ResumoDocumentosMaquina {
  total: number;
  pendentes: number;
  emAnalise: number;
  entregues: number;
  atrasados: number;
  /** Tipos que ainda não foram entregues — é o que o filtro do dashboard usa. */
  tiposPendentes: string[];
}

export function resumirDocumentosMaquina(
  maquinaId: string,
  agora: Date = new Date()
): ResumoDocumentosMaquina {
  const documentos = listarDocumentosMaquina(maquinaId);
  const emAberto = documentos.filter((doc) => doc.status !== 'entregue');

  return {
    total: documentos.length,
    pendentes: documentos.filter((doc) => doc.status === 'pendente').length,
    emAnalise: documentos.filter((doc) => doc.status === 'em_analise').length,
    entregues: documentos.filter((doc) => doc.status === 'entregue').length,
    atrasados: emAberto.filter((doc) => new Date(doc.prazo).getTime() < agora.getTime()).length,
    tiposPendentes: Array.from(new Set(emAberto.map((doc) => doc.tipo))),
  };
}

export function documentoAtrasado(doc: DocumentoMaquina, agora: Date = new Date()): boolean {
  return doc.status !== 'entregue' && new Date(doc.prazo).getTime() < agora.getTime();
}

// ---------------------------------------------------------------------- escrita

export interface NovoDocumentoMaquina {
  tipo: string;
  codigo: string;
  revisao: number;
  status: StatusDocumentoMaquina;
  responsavel: string;
  prazo: string;
  arquivo?: string;
  observacao?: string;
}

export function adicionarDocumentoMaquina(
  maquinaId: string,
  entrada: NovoDocumentoMaquina
): DocumentoMaquina {
  const overlay = lerOverlay();
  const daMaquina = overlayDaMaquina(overlay, maquinaId);

  const documento: DocumentoMaquina = {
    ...entrada,
    id: `${maquinaId}:local:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
    maquinaId,
    emitidoEm: entrada.status === 'pendente' ? null : new Date().toISOString(),
    origem: 'local',
  };

  overlay[maquinaId] = { ...daMaquina, adicionados: [...daMaquina.adicionados, documento] };
  gravarOverlay(overlay);
  return documento;
}

export function removerDocumentoMaquina(maquinaId: string, documentoId: string): void {
  const overlay = lerOverlay();
  const daMaquina = overlayDaMaquina(overlay, maquinaId);

  overlay[maquinaId] = {
    ...daMaquina,
    adicionados: daMaquina.adicionados.filter((doc) => doc.id !== documentoId),
    removidos: daMaquina.removidos.includes(documentoId)
      ? daMaquina.removidos
      : [...daMaquina.removidos, documentoId],
  };
  gravarOverlay(overlay);
}

export function alterarStatusDocumentoMaquina(
  maquinaId: string,
  documentoId: string,
  status: StatusDocumentoMaquina
): void {
  const overlay = lerOverlay();
  const daMaquina = overlayDaMaquina(overlay, maquinaId);

  overlay[maquinaId] = {
    ...daMaquina,
    adicionados: daMaquina.adicionados.map((doc) =>
      doc.id === documentoId
        ? { ...doc, status, emitidoEm: status === 'pendente' ? null : doc.emitidoEm ?? new Date().toISOString() }
        : doc
    ),
    status: { ...daMaquina.status, [documentoId]: status },
  };
  gravarOverlay(overlay);
}
